import React, { useState, useEffect, useCallback } from 'react';
import { Search, RefreshCw, UserPlus, CheckCircle2, Globe, Mail, Phone, Calendar, Loader2, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { getWebsiteApplicants, importWebsiteApplicant } from '../api';
import { User } from '../types';

interface WebsiteApplicant {
  id: string;
  email: string;
  name: string | null;
  phone: string | null;
  avatar: string | null;
  createdAt: string;
  applicationCount: number;
  resumes: any[];
  alreadyImported: boolean;
}

interface Props {
  currentUser: User;
  notificationBell?: React.ReactNode;
  onOpenCandidate?: (id: string) => void;
}

export default function WebsiteApplicants({ currentUser, notificationBell, onOpenCandidate }: Props) {
  const [applicants, setApplicants] = useState<WebsiteApplicant[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [importing, setImporting] = useState<Set<string>>(new Set());
  const [importedIds, setImportedIds] = useState<Set<string>>(new Set());

  const fetchApplicants = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getWebsiteApplicants({ page, pageSize, search });
      setApplicants(data.users || []);
      setTotal(data.total || 0);
    } catch (err: any) {
      toast.error(err.response?.data?.message || '获取官网投递失败');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search]);

  useEffect(() => { fetchApplicants(); }, [fetchApplicants]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput.trim());
    setPage(1);
  };

  const handleImport = async (applicant: WebsiteApplicant) => {
    setImporting(prev => new Set(prev).add(applicant.id));
    try {
      const result = await importWebsiteApplicant({
        email: applicant.email,
        name: applicant.name || undefined,
        phone: applicant.phone || undefined,
        external_id: applicant.id,
      });
      toast.success('已导入候选人池');
      setImportedIds(prev => new Set(prev).add(applicant.id));
      setApplicants(prev => prev.map(a => a.id === applicant.id ? { ...a, alreadyImported: true } : a));
      if (result.candidateId && onOpenCandidate) {
        onOpenCandidate(result.candidateId);
      }
    } catch (err: any) {
      if (err.response?.status === 409) {
        toast.error('该邮箱已在候选人库中');
        setApplicants(prev => prev.map(a => a.id === applicant.id ? { ...a, alreadyImported: true } : a));
      } else {
        toast.error(err.response?.data?.message || '导入失败');
      }
    } finally {
      setImporting(prev => { const s = new Set(prev); s.delete(applicant.id); return s; });
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit' });
    } catch { return iso; }
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="shrink-0 bg-white border-b border-gray-200 px-5 py-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2.5">
            {notificationBell}
            <div className="w-8 h-8 rounded-lg bg-teal-500 flex items-center justify-center">
              <Globe size={16} className="text-white" />
            </div>
            <div>
              <h1 className="text-base font-bold text-gray-900">官网投递</h1>
              <p className="text-[11px] text-gray-400">来自 career.manifoldtech.cn 的注册用户</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-full">共 {total} 人</span>
            <button
              onClick={fetchApplicants}
              className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
              title="刷新"
            >
              <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              placeholder="搜索姓名或邮箱..."
              className="w-full h-9 pl-9 pr-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300 focus:border-teal-300 bg-white"
            />
          </div>
          <button type="submit" className="px-4 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 transition-colors">
            搜索
          </button>
          {search && (
            <button type="button" onClick={() => { setSearch(''); setSearchInput(''); setPage(1); }}
              className="px-3 py-2 text-sm text-gray-500 hover:bg-gray-100 rounded-lg transition-colors">
              清除
            </button>
          )}
        </form>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-5 py-4">
        {loading ? (
          <div className="flex items-center justify-center h-40 text-gray-400">
            <Loader2 size={24} className="animate-spin mr-2" />加载中...
          </div>
        ) : applicants.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-gray-400">
            <Globe size={32} className="mb-2 opacity-40" />
            <p className="text-sm">{search ? '没有找到匹配的投递用户' : '暂无官网注册用户'}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {applicants.map(applicant => {
              const isImporting = importing.has(applicant.id);
              const isImported = applicant.alreadyImported || importedIds.has(applicant.id);
              const displayName = applicant.name || applicant.email?.split('@')[0] || '—';

              return (
                <div key={applicant.id}
                  className="bg-white rounded-xl border border-gray-200 px-4 py-3.5 flex items-center gap-4 hover:border-teal-200 hover:shadow-sm transition-all">

                  {/* Avatar */}
                  <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center shrink-0">
                    {applicant.avatar
                      ? <img src={applicant.avatar} alt="" className="w-10 h-10 rounded-full object-cover" />
                      : <span className="text-sm font-bold text-teal-600">{displayName.charAt(0).toUpperCase()}</span>
                    }
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-gray-800">{displayName}</span>
                      {isImported && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-green-50 text-green-600 rounded text-[10px] font-medium">
                          <CheckCircle2 size={10} />已在候选人库
                        </span>
                      )}
                      {applicant.applicationCount > 0 && (
                        <span className="px-1.5 py-0.5 bg-teal-50 text-teal-600 rounded text-[10px]">
                          投递 {applicant.applicationCount} 次
                        </span>
                      )}
                      {applicant.resumes.length > 0 && (
                        <span className="px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded text-[10px]">
                          {applicant.resumes.length} 份简历
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-[11px] text-gray-400 flex-wrap">
                      {applicant.email && (
                        <span className="flex items-center gap-1">
                          <Mail size={11} />{applicant.email}
                        </span>
                      )}
                      {applicant.phone && (
                        <span className="flex items-center gap-1">
                          <Phone size={11} />{applicant.phone}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Calendar size={11} />注册于 {formatDate(applicant.createdAt)}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="shrink-0">
                    {isImported ? (
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <CheckCircle2 size={14} className="text-green-500" />已导入
                      </span>
                    ) : (
                      <button
                        onClick={() => handleImport(applicant)}
                        disabled={isImporting}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg transition-colors disabled:opacity-50"
                      >
                        {isImporting
                          ? <><Loader2 size={12} className="animate-spin" />导入中</>
                          : <><UserPlus size={12} />导入候选人</>
                        }
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="shrink-0 bg-white border-t border-gray-200 px-5 py-3 flex items-center justify-between">
          <span className="text-xs text-gray-500">第 {page} / {totalPages} 页，共 {total} 条</span>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-40 transition-colors">
              <ChevronLeft size={16} />
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const p = Math.max(1, Math.min(page - 2 + i, totalPages - 4 + i));
              return (
                <button key={p} onClick={() => setPage(p)}
                  className={clsx('w-7 h-7 rounded-lg text-xs transition-colors', p === page ? 'bg-teal-600 text-white' : 'text-gray-500 hover:bg-gray-100')}>
                  {p}
                </button>
              );
            })}
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-40 transition-colors">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
